"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "../lib/utils";
interface ConversationalContainerProps {
  children: React.ReactNode;
  currentStep: number;
  totalSteps: number;
  onNext?: () => void;
  onPrevious?: (() => void) | undefined;
  showVehicleInfo?: boolean;
  vehicleInfo?: {
    year: number;
    make: string;
    model: string;
  };
  className?: string;
}
interface ProgressDotsProps {
  current: number;
  total: number;
  className?: string;
}
const ProgressDots: React.FC<ProgressDotsProps> = t0 => {
  const $ = _c(10);
  const {
    current,
    total,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("t-flex t-items-center t-justify-center t-space-x-2", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== current || $[3] !== total) {
    let t3;
    if ($[5] !== current) {
      t3 = (_, index) => <motion.div key={index} className={cn("t-h-2 t-w-2 t-rounded-full t-transition-all t-duration-300", index < current ? "t-bg-automotive-primary t-shadow-sm" : index === current ? "t-bg-automotive-primary t-w-8 t-shadow-md" : "t-bg-gray-300")} initial={{
        scale: 0.8
      }} animate={{
        scale: index === current ? 1.1 : 1,
        opacity: index <= current ? 1 : 0.5
      }} transition={{
        duration: 0.2
      }} />;
      $[5] = current;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    t2 = Array.from({
      length: total
    }, t3);
    $[2] = current;
    $[3] = total;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[7] !== t1 || $[8] !== t2) {
    t3 = <div className={t1}>{t2}</div>;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
};
interface VehicleInfoBadgeProps {
  vehicleInfo: {
    year: number;
    make: string;
    model: string;
  };
}
const VehicleInfoBadge: React.FC<VehicleInfoBadgeProps> = t0 => {
  const $ = _c(7);
  const {
    vehicleInfo
  } = t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      opacity: 0,
      y: -20
    };
    t2 = {
      opacity: 1,
      y: 0
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <svg className="t-h-4 t-w-4 t-text-automotive-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== vehicleInfo.make || $[4] !== vehicleInfo.model || $[5] !== vehicleInfo.year) {
    t4 = <motion.div initial={t1} animate={t2} className="t-fixed t-top-4 t-left-4 t-z-10 t-md:t-top-6 t-md:t-left-6"><div className="t-bg-white/95 t-backdrop-blur-sm t-rounded-full t-px-4 t-py-2 t-shadow-lg t-border t-border-gray-200"><div className="t-flex t-items-center t-space-x-2">{t3}<span className="t-text-sm t-font-medium t-text-gray-700">{vehicleInfo.year} {vehicleInfo.make} {vehicleInfo.model}</span></div></div></motion.div>;
    $[3] = vehicleInfo.make;
    $[4] = vehicleInfo.model;
    $[5] = vehicleInfo.year;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
};
export const ConversationalContainer: React.FC<ConversationalContainerProps> = t0 => {
  const $ = _c(35);
  const {
    children,
    currentStep,
    totalSteps,
    onNext,
    onPrevious,
    showVehicleInfo: t1,
    vehicleInfo,
    className
  } = t0;
  const showVehicleInfo = t1 === undefined ? true : t1;
  const containerRef = useRef(null);
  let t2;
  if ($[0] !== onPrevious) {
    t2 = () => {
      const handleKeyDown = event => {
        const target = event.target as HTMLElement;
        if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA")) {
          return;
        }
        bb18: switch (event.key) {
          case "Enter":
            {
              break bb18;
            }
          case "Backspace":
            {
              event.preventDefault();
              onPrevious?.();
              break bb18;
            }
          case "ArrowRight":
            {
              break bb18;
            }
          case "ArrowLeft":
            {
              event.preventDefault();
              onPrevious?.();
              break bb18;
            }
          case "1":
          case "2":
          case "3":
          case "4":
          case "5":
          case "6":
          case "7":
          case "8":
          case "9":
            {
              event.preventDefault();
              const numberEvent = new CustomEvent("numberKeyPress", {
                detail: {
                  number: parseInt(event.key, 10)
                }
              });
              document.dispatchEvent(numberEvent);
            }
        }
      };
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    };
    $[0] = onPrevious;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== onNext || $[3] !== onPrevious) {
    t3 = [onNext, onPrevious];
    $[2] = onNext;
    $[3] = onPrevious;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      const timeoutId = setTimeout(() => {
        if (containerRef.current) {
          containerRef.current.focus();
        }
        window.scrollTo({
          top: 0,
          behavior: "smooth"
        });
      }, 100);
      return () => clearTimeout(timeoutId);
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== currentStep) {
    t5 = [currentStep];
    $[6] = currentStep;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  useEffect(t4, t5);
  let t6;
  if ($[8] !== className) {
    t6 = cn("t-min-h-screen t-bg-gradient-to-br t-from-slate-50 t-to-gray-100", "t-flex t-flex-col t-relative t-overflow-hidden", className);
    $[8] = className;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== showVehicleInfo || $[11] !== vehicleInfo) {
    t7 = showVehicleInfo && vehicleInfo && <VehicleInfoBadge vehicleInfo={vehicleInfo} />;
    $[10] = showVehicleInfo;
    $[11] = vehicleInfo;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== currentStep || $[14] !== totalSteps) {
    t8 = <div className="t-fixed t-top-4 t-right-4 t-z-10 t-md:t-top-6 t-md:t-right-6"><ProgressDots current={currentStep} total={totalSteps} /></div>;
    $[13] = currentStep;
    $[14] = totalSteps;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t10;
  let t11;
  let t12;
  let t9;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      opacity: 0
    };
    t10 = {
      opacity: 1
    };
    t11 = {
      opacity: 0
    };
    t12 = {
      duration: 0.25,
      ease: "easeInOut"
    };
    $[16] = t10;
    $[17] = t11;
    $[18] = t12;
    $[19] = t9;
  } else {
    t10 = $[16];
    t11 = $[17];
    t12 = $[18];
    t9 = $[19];
  }
  let t13;
  if ($[20] !== children || $[21] !== currentStep) {
    t13 = <div className="t-flex-1 t-flex t-items-center t-justify-center t-p-4 t-md:t-p-8"><div className="t-w-full t-max-w-2xl t-min-h-[600px] t-flex t-flex-col t-justify-center"><AnimatePresence mode="wait" initial={false}><motion.div key={currentStep} initial={t9} animate={t10} exit={t11} transition={t12} className="t-w-full">{children}</motion.div></AnimatePresence></div></div>;
    $[20] = children;
    $[21] = currentStep;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== currentStep) {
    t14 = currentStep > 0 && <div className="t-flex t-items-center t-space-x-1"><kbd className="t-px-1.5 t-py-0.5 t-bg-gray-200 t-rounded t-text-xs">←</kbd><span>Back</span></div>;
    $[23] = currentStep;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <div className="t-flex t-items-center t-space-x-1"><kbd className="t-px-1.5 t-py-0.5 t-bg-gray-200 t-rounded t-text-xs">Enter</kbd><span>Continue</span></div>;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== t14) {
    t16 = <div className="t-hidden t-md:t-block t-fixed t-bottom-4 t-left-1/2 t-transform t--translate-x-1/2 t-z-10"><div className="t-bg-white/80 t-backdrop-blur-sm t-rounded-full t-px-4 t-py-2 t-shadow-sm t-border t-border-gray-200"><div className="t-flex t-items-center t-space-x-4 t-text-xs t-text-gray-500">{t14}{t15}</div></div></div>;
    $[26] = t14;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <div className="t-absolute t-inset-0 t-opacity-5 t-pointer-events-none"><div className="t-absolute t-inset-0" style={{
        backgroundImage: "radial-gradient(circle at 20% 50%, currentColor 1px, transparent 1px),\n                             radial-gradient(circle at 80% 50%, currentColor 1px, transparent 1px)",
        backgroundSize: "200px 200px, 200px 200px"
      }} /></div>;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  let t18;
  if ($[29] !== t13 || $[30] !== t16 || $[31] !== t6 || $[32] !== t7 || $[33] !== t8) {
    t18 = <div ref={containerRef} className={t6} tabIndex={-1}>{t7}{t8}{t13}{t16}{t17}</div>;
    $[29] = t13;
    $[30] = t16;
    $[31] = t6;
    $[32] = t7;
    $[33] = t8;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  return t18;
};